/**
 * SessionTracker - セッショントラッキングメインクラス
 *
 * EventCollectorとEventQueueを統合し、ch-serverへイベント送信を管理
 */

import type { TrackedEvent, TalknTunePayload, TalknPostPayload, TalknFetchPayload } from '@common/models';
import type { SessionTrackerConfig, SendEventResult } from './types';
import { EventCollector } from './EventCollector';
import { EventQueue } from './EventQueue';

// StoresDispatcherの型（循環参照回避）
type StoresDispatcher = {
  postMessage: (message: any) => Promise<any>;
};

export class SessionTracker {
  private config: SessionTrackerConfig;
  private trackingUserId: string | null = null;
  private socketSessionId: string | null = null;
  private eventCollector: EventCollector;
  private eventQueue: EventQueue;
  private dispatcher: StoresDispatcher | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isEnabled: boolean = false;
  private isStarted: boolean = false;
  private readonly handleBeforeUnload: () => void;
  private readonly handlePageHide: () => void;

  constructor(config: SessionTrackerConfig) {
    this.config = {
      batchSize: 10,
      flushInterval: 5000,
      debug: false,
      ...config,
    };

    this.eventCollector = new EventCollector();
    this.eventQueue = new EventQueue();

    // イベントコールバック設定
    this.eventCollector.setEventCallback((event) => {
      this.handleEvent(event);
    });

    this.isEnabled = config.enabled;
    this.handleBeforeUnload = this.flushSync.bind(this);
    this.handlePageHide = this.flushSync.bind(this);
  }

  /**
   * トラッキング開始
   * @param trackingUserId - 継続的な匿名ユーザーID
   * @param socketSessionId - ch-serverから受け取ったセッションID
   * @param dispatcher - StoresDispatcherインスタンス
   */
  async start(trackingUserId: string, socketSessionId: string | undefined, dispatcher: StoresDispatcher): Promise<void> {
    if (!this.isEnabled) {
      if (this.config.debug) {
        console.log('[SessionTracker] Tracking disabled');
      }
      return;
    }

    if (this.isStarted) {
      this.updateTransport(trackingUserId, socketSessionId, dispatcher);
      return;
    }

    this.trackingUserId = trackingUserId;
    this.socketSessionId = socketSessionId || null;
    this.dispatcher = dispatcher;

    this.eventCollector.setIdentity(trackingUserId, socketSessionId);

    // 自動トラッキング開始
    this.eventCollector.startAutoTracking();

    // 定期フラッシュ開始
    this.startPeriodicFlush();

    // セッション開始イベント
    await this.trackSessionStart();

    if (this.config.debug) {
      console.log('[SessionTracker] Started with trackingUserId:', trackingUserId, 'socketSessionId:', socketSessionId);
    }

    this.isStarted = true;

    // ページ離脱時にフラッシュ
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.handleBeforeUnload);
      window.addEventListener('pagehide', this.handlePageHide);
    }
  }

  updateTransport(trackingUserId: string, socketSessionId: string | undefined, dispatcher: StoresDispatcher): void {
    this.trackingUserId = trackingUserId;
    this.socketSessionId = socketSessionId || null;
    this.dispatcher = dispatcher;
    this.eventCollector.setIdentity(trackingUserId, socketSessionId);
  }

  isActive(): boolean {
    return this.isStarted;
  }

  /**
   * トラッキング停止
   */
  stop(): void {
    if (!this.isStarted) {
      return;
    }

    this.eventCollector.stopAutoTracking();

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // 残りイベントをフラッシュ
    this.flushSync();

    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      window.removeEventListener('pagehide', this.handlePageHide);
    }

    this.isStarted = false;

    if (this.config.debug) {
      console.log('[SessionTracker] Stopped');
    }
  }

  /**
   * TalknAPI操作トラッキング
   * @param action - 操作名（tune, untune, post, fetch）
   * @param connection - チャンネル接続
   * @param metadata - 追加メタデータ
   */
  trackTalknAction(
    action: 'tune' | 'untune' | 'post' | 'fetch',
    connection: string,
    metadata?: any
  ): void {
    if (!this.isEnabled || !this.trackingUserId) return;

    const eventType = `talkn_${action}` as any;
    let payload: any;

    switch (action) {
      case 'tune':
        payload = {
          type: eventType,
          connection,
          tuneId: metadata?.tuneId || '',
          tuneOptions: metadata?.tuneOptions || {},
        } as TalknTunePayload;
        break;

      case 'post':
        payload = {
          type: eventType,
          connection,
          contentLength: metadata?.contentLength || 0,
          stampId: metadata?.stampId,
        } as TalknPostPayload;
        break;

      case 'fetch':
        payload = {
          type: eventType,
          connection,
          method: metadata?.method || 'fetchPosts',
        } as TalknFetchPayload;
        break;

      default:
        payload = {
          type: eventType,
          connection,
          ...metadata,
        };
    }

    const event: TrackedEvent = {
      eventId: this.generateEventId(),
      sessionId: this.trackingUserId,
      trackingUserId: this.trackingUserId,
      socketSessionId: this.socketSessionId || undefined,
      timestamp: Date.now(),
      eventType,
      payload,
    };

    this.handleEvent(event);
  }

  /**
   * イベント処理（キューに追加し、必要に応じてフラッシュ）
   * @param event - トラッキングイベント
   */
  private handleEvent(event: TrackedEvent): void {
    this.eventQueue.enqueue(event);

    if (this.config.debug) {
      console.log('[SessionTracker] Event queued:', event.eventType, event.payload);
    }

    // バッチサイズに達したら即座にフラッシュ
    if (this.eventQueue.size() >= (this.config.batchSize || 10)) {
      this.flush().catch((error) => {
        console.error('[SessionTracker] Flush error:', error);
      });
    }
  }

  /**
   * キューをフラッシュ（非同期）
   */
  async flush(): Promise<SendEventResult> {
    if (!this.dispatcher || !this.trackingUserId) {
      return {
        success: false,
        sent: 0,
        failed: 0,
        errors: ['No dispatcher or trackingUserId'],
      };
    }

    const batchSize = this.config.batchSize || 10;
    const events = this.eventQueue.peek(batchSize);

    if (events.length === 0) {
      return { success: true, sent: 0, failed: 0 };
    }

    try {
      // trackSessionエンドポイントにPOST
      const response = await this.dispatcher.postMessage({
        method: 'trackSession',
        apiState: {
          events,
        },
      });

      if (!response?.payload || response.payload.success !== true) {
        return {
          success: false,
          sent: 0,
          failed: events.length,
          errors: Array.isArray(response?.payload?.errors)
            ? (response.payload.errors as string[])
            : ['trackSession was not acknowledged'],
        };
      }

      // 送信成功したイベントをキューから削除
      this.eventQueue.dequeue(events.length);

      if (this.config.debug) {
        console.log('[SessionTracker] Flushed', events.length, 'events');
      }

      if (this.config.onSendSuccess) {
        this.config.onSendSuccess(events.length);
      }

      return {
        success: true,
        sent: events.length,
        failed: 0,
      };
    } catch (error: any) {
      console.error('[SessionTracker] Failed to send events:', error);

      if (this.config.onSendError) {
        this.config.onSendError(error);
      }

      return {
        success: false,
        sent: 0,
        failed: events.length,
        errors: [error.message],
      };
    }
  }

  /**
   * キューを同期的にフラッシュ（beforeunload用）
   */
  private flushSync(): void {
    if (!this.dispatcher || !this.trackingUserId) return;

    const events = this.eventQueue.peek(this.config.batchSize || 10);
    if (events.length === 0) return;

    void this.flush().catch((error) => {
      console.error('[SessionTracker] Sync flush error:', error);
    });
  }

  /**
   * 定期フラッシュ開始
   */
  private startPeriodicFlush(): void {
    const interval = this.config.flushInterval || 5000;

    this.flushTimer = setInterval(() => {
      this.flush().catch((error) => {
        console.error('[SessionTracker] Periodic flush error:', error);
      });
    }, interval);
  }

  /**
   * セッション開始イベント
   */
  private async trackSessionStart(): Promise<void> {
    if (!this.trackingUserId) return;

    const event: TrackedEvent = {
      eventId: this.generateEventId(),
      sessionId: this.trackingUserId,
      trackingUserId: this.trackingUserId,
      socketSessionId: this.socketSessionId || undefined,
      timestamp: Date.now(),
      eventType: 'session_start',
      payload: {
        type: 'session_start',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        screenResolution:
          typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : 'unknown',
        language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      },
    };

    this.handleEvent(event);
  }

  /**
   * イベントID生成
   */
  private generateEventId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
